import React, { useState, useEffect, useContext } from 'react';
import { AdminAuthContext } from '../../AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiBriefcase, FiEdit2, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import './AdminUI.css';

const Companies = () => {
  const { admin, loading } = useContext(AdminAuthContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && admin && admin.role === 'admin') {
      fetch('https://valleyapi-dr9y.onrender.com/api/admin/users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          setUsers(data || []);
          setFilteredUsers(data || []);
          setError(data?.length ? null : 'No company data received');
        })
        .catch((fetchError) => {
          console.error('Error loading users:', fetchError);
          setError('Failed to load companies');
        });
    }
  }, [loading, admin]);

  useEffect(() => {
    const query = searchTerm.toLowerCase();
    setFilteredUsers(users.filter((user) =>
      (user.name || '').toLowerCase().includes(query) ||
      (user.companyName || '').toLowerCase().includes(query) ||
      (user.companyNumber || '').toLowerCase().includes(query) ||
      (user.country || '').toLowerCase().includes(query)
    ));
  }, [searchTerm, users]);

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      setUsers((current) => current.filter((user) => user.idusers !== userId));
      alert('Company deleted successfully');
    } catch (deleteError) {
      console.error('Error deleting user:', deleteError);
      alert('The company could not be deleted');
    }
  };

  if (loading) return <div className="va-page va-card va-loading"><div className="va-spinner" aria-label="Loading companies" /></div>;
  if (!admin || admin.role !== 'admin') return <div className="va-page va-card va-state"><div><FiAlertTriangle /><strong>Access denied</strong><p>Administrator privileges are required.</p></div></div>;

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Customer management</span>
          <h1 className="va-title">Companies</h1>
          <p className="va-subtitle">Manage customer organizations, contacts, verification and account details.</p>
        </div>
        <div className="va-header-actions">
          <button className="va-button" onClick={() => navigate('/admin/companies/add')}><FiPlus /> Add company</button>
        </div>
      </header>
      
      <div className="va-toolbar">
        <div className="va-search"><FiSearch /><input type="search" placeholder="Search name, company, number or country" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} aria-label="Search companies" /></div>
        <span className="va-results">{filteredUsers.length} result{filteredUsers.length === 1 ? '' : 's'}</span>
      </div>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}

      <div className="va-table-wrap">
        <table className="va-table">
          <thead><tr><th>Company</th><th>Primary contact</th><th>Company no.</th><th>Country</th><th>Verification</th><th></th></tr></thead>
          <tbody>
            {filteredUsers.length > 0 ? filteredUsers.map((user) => (
              <tr key={user.idusers}>
                <td><span className="va-table__primary">{user.companyName || 'Unnamed company'}</span><span className="va-table__secondary">ID #{user.idusers || 'N/A'}</span></td>
                <td>{user.name || 'N/A'}</td>
                <td className="va-number">{user.companyNumber || 'N/A'}</td>
                <td>{user.country || 'N/A'}</td>
                <td><span className={`va-badge ${user.verified === 1 ? 'va-badge--success' : 'va-badge--warning'}`}>{user.verified === 1 ? 'Verified' : 'Pending'}</span></td>
                <td><div className="va-table__actions">
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => navigate(`/admin/companies/edit/${user.idusers}`)} title="Edit company" aria-label={`Edit ${user.companyName}`}><FiEdit2 /></button>
                  <button className="va-button va-button--ghost va-button--icon" onClick={() => handleDeleteUser(user.idusers)} title="Delete company" aria-label={`Delete ${user.companyName}`}><FiTrash2 /></button>
                </div></td>
              </tr>
            )) : (
              <tr><td colSpan="6"><div className="va-empty"><div><FiBriefcase /><strong>No companies found</strong><p>Try a different search or add a new company.</p></div></div></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Companies;
